import { MDXRemote, MDXRemoteSerializeResult } from "next-mdx-remote";
import React from "react";
import { Container } from "react-grid-system";
import { TrackedLink } from "@components/analytics/TrackedLink";
import Breadcrumbs from "@components/Breadcrumbs";
import AuthorMetaData from "@components/blog/AuthorMetaData";
import BlogPost from "@components/blog/BlogPost";
import CopyButton from "@components/blog/buttons/CopyButton";
import EmailButton from "@components/blog/buttons/EmailButton";
import LinkedInButton from "@components/blog/buttons/LinkedInButton";
import Sidebar from "@components/layout/Sidebar";
import { MDXComponentMap } from "@components/MDXComponentMap";
import { NewsletterContainerDark } from "@components/newsletter/NewsletterContainerDark";
import { NewsletterSignup } from "@components/newsletter/NewsletterSignup";
import Tags from "@components/Tags";
import { ImgSrc } from "@config/image";
import theme from "@config/theme";
import { formatPublishDate, stringToURI } from "@scripts/utils";

interface BlogPostContentProps {
  metadata: BlogPost;
  postSource: MDXRemoteSerializeResult;
  tocSource: MDXRemoteSerializeResult;
}

const BlogPostContent = ({
  metadata,
  postSource,
  tocSource,
}: BlogPostContentProps) => (
  <>
    <Container>
      <div className="blog-post-page">
        <Breadcrumbs
          links={[
            { name: "Blog", url: "/blog" },
            {
              name: metadata.title,
              url: `/blog/${metadata.slug}`,
            },
          ]}
        />

        <h1 className="blog-post-title">{metadata.title}</h1>

        <div className="blog-post-heading">
          <div className="blog-post-hero">
            <img
              alt={`${metadata.title} banner`}
              src={ImgSrc(metadata.banner)}
            />
          </div>

          <div>
            <p className="blog-post-date">
              {formatPublishDate(metadata.publishedAt)}
            </p>
            <h2 className="blog-post-excerpt">{metadata.excerpt}</h2>
            <div className="blog-post-tags">
              <Tags
                tags={metadata.tags.map((tag) => ({
                  name: tag,
                  url: `/blog/tag/${stringToURI(tag)}`,
                }))}
              />
            </div>
            <AuthorMetaData author={metadata.author} />

            <div className="blog-share-buttons">
              <LinkedInButton />
              <CopyButton />
              <EmailButton />
            </div>
          </div>
        </div>

        <div className="blog-content">
          <div>
            {/* @ts-expect-error Server Component */}
            <MDXRemote {...postSource} components={MDXComponentMap} />
          </div>
          <Sidebar>
            <>
              <h3 className="in-this-article-header">In This Article</h3>
              {/* @ts-expect-error Server Component */}
              <MDXRemote {...tocSource} components={MDXComponentMap} />

              <h3 className="dev-news-header">Blues Developer News</h3>
              <p>
                The latest IoT news for developers, delivered right to your
                inbox.
              </p>

              <NewsletterSignup pageSource={`Blog ${metadata.title} Page`} />
            </>
          </Sidebar>
        </div>

        <div className="blog-footer">
          <h2 id="comments">Comments</h2>
          <p>
            <TrackedLink
              href={`https://discuss.blues.com/t/${stringToURI(metadata.title, {
                discourseLink: true,
              })}`}
            >
              Join the conversation for this article
            </TrackedLink>{" "}
            on our Community Forum
          </p>
        </div>
      </div>
    </Container>

    <div className="blog-post-newsletter">
      <NewsletterContainerDark pageSource={`Blog ${metadata.title} Page`} />
    </div>

    <style jsx>{`
      .blog-post-page {
        padding: 0 1rem;
        max-width: 1140px;
        margin: 0 auto;
      }
      .blog-post-heading {
        display: flex;
      }

      .blog-post-hero img {
        max-width: 640px;
        height: auto;
        border-radius: 4px;
        box-shadow: ${theme.misc.boxShadowBlogImage};
        margin-right: 2rem;
      }

      .blog-post-date {
        color: ${theme.colors.gray3};
        margin: 0;
      }

      .blog-post-excerpt {
        font-weight: 400;
        margin-top: 1rem;
        margin-bottom: 2rem;
        font-size: ${theme.fonts.size.px26};
      }

      .blog-share-buttons {
        margin-top: 1.5rem;
        display: flex;
        gap: 0.5rem;
      }

      .blog-content {
        display: flex;
        margin: 3rem 0 0 0;
        border-top: 1px solid ${theme.colors.gray4};
        padding-top: 2rem;
        gap: 2rem;
      }
      .blog-content > div:first-child {
        overflow: hidden;
      }

      :global(.sidebar) p {
        color: ${theme.colors.gray3};
        margin-bottom: 0;
        margin-top: 0;
      }
      :global(.sidebar) h3 {
        margin-bottom: 1rem;
      }
      .in-this-article {
        margin-top: 1rem;
        margin-bottom: 1rem;
      }
      .dev-news-header {
        margin-top: 2rem;
      }

      // styling for markdown content
      :global(li .doc-image) {
        padding: 1rem 0;
      }

      :global(.blog-content a:hover svg.anchor-icon) {
        display: none;
      }
      :global(.blog-content a:hover .hash-icon) {
        visibility: visible;
        display: inline-block;
      }

      .blog-footer p {
        background-image: url("${ImgSrc("/images/icons/chat.svg")}");
        background-repeat: no-repeat;
        margin-top: 1.5rem;
        padding-left: 2rem;
      }
      .blog-footer h2 {
        border-top: 1px solid ${theme.colors.gray4};
        padding-top: 1.5rem;
      }

      .blog-post-newsletter {
        margin-top: 3rem;
      }

      @media screen and (max-width: ${theme.breakpoints.md}px) {
        .blog-post-heading {
          display: block;
        }
        .blog-post-hero img {
          max-width: 100%;
        }
        .blog-post-date {
          margin-top: 1rem;
        }
        .blog-post-excerpt {
          margin-bottom: 1rem;
        }
        :global(.blog-post-page .author) {
          margin-top: 1.5rem;
        }
        .blog-content {
          display: block;
        }
        :global(.sidebar) {
          display: none;
        }
      }
    `}</style>
  </>
);

export default BlogPostContent;
